import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const getAdminCrudKeys = (resourceKey) => ({
    all: ['admin', resourceKey],
    lists: ['admin', resourceKey, 'list'],
    list: (params) => ['admin', resourceKey, 'list', params],
    details: ['admin', resourceKey, 'detail'],
    detail: (id) => ['admin', resourceKey, 'detail', id],
});

export const normalizeAdminList = (response, params = {}) => ({
    items: Array.isArray(response?.data) ? response.data : response?.data?.items || [],
    total: Array.isArray(response?.data) ? response.data.length : response?.data?.totalItems || response?.data?.total || response?.data?.pagination?.totalItems || 0,
    currentPage: response?.data?.pageIndex || response?.data?.currentPage || response?.data?.current || response?.data?.pagination?.currentPage || params.pageIndex || params.pageNumber || 1,
    pageSize: response?.data?.pageSize || response?.data?.pagination?.pageSize || params.pageSize || 10,
});

export const normalizeAdminDetail = (response) => {
    if (!response?.success || !response.data) return null;

    return {
        ...response.data,
        translation: response.data.translations || response.data.translation || [],
    };
};

export const useAdminListQuery = (resourceKey, params, listFn, options = {}) => (
    useQuery({
        queryKey: getAdminCrudKeys(resourceKey).list(params),
        queryFn: () => listFn(params),
        select: (response) => normalizeAdminList(response, params),
        placeholderData: keepPreviousData,
        ...options,
    })
);

export const useAdminDetailQuery = (resourceKey, id, detailFn, options = {}) => (
    useQuery({
        queryKey: getAdminCrudKeys(resourceKey).detail(id),
        queryFn: () => detailFn(id),
        enabled: Boolean(id),
        select: normalizeAdminDetail,
        ...options,
    })
);

export const useAdminCreateMutation = (resourceKey, createFn) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: createFn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getAdminCrudKeys(resourceKey).lists }),
    });
};

export const useAdminUpdateMutation = (resourceKey, updateFn) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }) => updateFn(id, data),
        onSuccess: (_response, variables) => {
            const keys = getAdminCrudKeys(resourceKey);
            queryClient.invalidateQueries({ queryKey: keys.lists });
            queryClient.invalidateQueries({ queryKey: keys.detail(variables.id) });
        },
    });
};

export const useAdminDeleteMutation = (resourceKey, deleteFn) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: deleteFn,
        onSuccess: () => queryClient.invalidateQueries({ queryKey: getAdminCrudKeys(resourceKey).lists }),
    });
};
